import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { rateLimit } from "@/lib/rate-limit.server";

const targetSchema = z
  .object({
    productId: z.string().uuid().optional(),
    vendorId: z.string().uuid().optional(),
  })
  .refine((d) => !!d.productId !== !!d.vendorId, {
    message: "Specify exactly one of productId or vendorId",
  });

export const listReviews = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => targetSchema.parse(input))
  .handler(async ({ data }) => {
    let q = supabaseAdmin
      .from("reviews")
      .select("id, rating, body, created_at, profile_id, product_id, vendor_id")
      .order("created_at", { ascending: false })
      .limit(100);


    q = data.productId ? q.eq("product_id", data.productId) : q.eq("vendor_id", data.vendorId!);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);

    const profileIds = Array.from(new Set((rows ?? []).map((r) => r.profile_id)));
    let profiles: Record<string, { display_name: string | null; first_name: string | null }> = {};
    if (profileIds.length > 0) {
      const { data: ps } = await supabaseAdmin
        .from("profiles")
        .select("id, display_name, first_name")
        .in("id", profileIds);
      profiles = Object.fromEntries((ps ?? []).map((p) => [p.id, p]));
    }

    // Javni odgovor namenoma ne vsebuje profile_id (interni identifikator profila).
    const reviews = (rows ?? []).map((r) => ({
      id: r.id,
      rating: r.rating,
      body: r.body,
      created_at: r.created_at,
      product_id: r.product_id,
      vendor_id: r.vendor_id,
      author_name:
        profiles[r.profile_id]?.display_name ||
        profiles[r.profile_id]?.first_name ||
        "Anonimni kupec",
    }));

    const count = reviews.length;
    const avg = count > 0 ? reviews.reduce((s, r) => s + r.rating, 0) / count : 0;
    return { reviews, count, avg };
  });

const upsertSchema = z.object({
  productId: z.string().uuid().optional(),
  vendorId: z.string().uuid().optional(),
  rating: z.number().int().min(1).max(5),
  body: z.string().max(2000).optional(),
});

export const upsertReview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => {
    const parsed = upsertSchema.parse(input);
    if (!!parsed.productId === !!parsed.vendorId) {
      throw new Error("Specify exactly one of productId or vendorId");
    }
    return parsed;
  })
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    await rateLimit(`review:${userId}`, 10, 60);

    const { data: profile, error: pErr } = await supabase
      .from("profiles")
      .select("id")
      .eq("auth_user_id", userId)
      .maybeSingle();
    if (pErr) throw new Error(pErr.message);
    if (!profile) throw new Error("Profil ne obstaja");

    // Verified-purchase check: product reviews require a paid order containing the product;
    // vendor reviews require a paid order from any product of that vendor.
    if (data.productId) {
      const { data: purchased } = await supabaseAdmin.rpc("has_purchased", {
        _auth_user_id: userId,
        _product_id: data.productId,
      });
      if (!purchased) throw new Error("Oceno lahko oddate šele po opravljenem nakupu.");
    } else if (data.vendorId) {
      const { data: matches } = await supabaseAdmin
        .from("order_items")
        .select("id, order:orders(status, profile_id)")
        .eq("vendor_id", data.vendorId)
        .limit(50);
      const hasBought = (matches ?? []).some((m) => {
        const o = (m as unknown as { order: { status: string; profile_id: string } | null }).order;
        return o && o.profile_id === profile.id && ["paid", "shipped", "delivered"].includes(o.status);
      });
      if (!hasBought) throw new Error("Prodajalca lahko ocenite šele po opravljenem nakupu.");
    }

    const payload = {
      profile_id: profile.id,
      product_id: data.productId ?? null,
      vendor_id: data.vendorId ?? null,
      rating: data.rating,
      body: data.body ?? null,
    };

    const matchCol = data.productId ? "product_id" : "vendor_id";
    const matchVal = data.productId ?? data.vendorId!;

    const { data: existing } = await supabase
      .from("reviews")
      .select("id")
      .eq("profile_id", profile.id)
      .eq(matchCol, matchVal)
      .maybeSingle();

    if (existing) {
      const { error } = await supabase
        .from("reviews")
        .update({ rating: payload.rating, body: payload.body })
        .eq("id", existing.id);
      if (error) throw new Error(error.message);
      return { id: existing.id };
    } else {
      const { data: inserted, error } = await supabase
        .from("reviews")
        .insert(payload)
        .select("id")
        .single();
      if (error) throw new Error(error.message);
      return { id: inserted.id };
    }
  });

export const deleteReview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    // Ownership check in code (defense in depth on top of RLS)
    const { data: profile } = await supabase
      .from("profiles").select("id").eq("auth_user_id", userId).maybeSingle();
    if (!profile) throw new Error("Profil ne obstaja");

    const { data: review } = await supabaseAdmin
      .from("reviews").select("profile_id").eq("id", data.id).maybeSingle();
    if (!review) throw new Error("Ocena ne obstaja.");

    const { data: roles } = await supabaseAdmin
      .from("user_roles").select("role").eq("profile_id", profile.id);
    const isAdmin = (roles ?? []).some((r) => String(r.role) === "admin");

    if (review.profile_id !== profile.id && !isAdmin) {
      throw new Error("Ni dovoljenja.");
    }

    const { error } = await supabaseAdmin.from("reviews").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
